import React, { useEffect, useMemo, useState } from "react";
import { generateClient } from "aws-amplify/api";
import { FiAlertTriangle } from "react-icons/fi";
import { listProcessedModelSchemasWithOutputs } from "../../graphql/customQueries";
import { datasetPickVerdict } from "../../utils/historicalModelRuns";
import type { FieldDefinition } from "../../types/modelSchemas";
import type { DatasetFieldContext } from "./DatasetField";

export interface ModelOutputFieldValue {
  type?: "model_output";
  outputParamId?: string | null;
  modelId?: string | null;
  storageMode?: "FILES";
  columns?: string[];
}

interface PickableOutput {
  id: string;
  name: string;
  unit: string | null;
  storageMode: "FILES";
  active: boolean;
  columns: string[];
}

interface PickableModel {
  id: string;
  name: string;
  modelKind: "HISTORICAL" | "FORECAST";
  organizationId: string | null;
  owner: string | null;
  outputs: PickableOutput[];
}

/** The rows listProcessedModelSchemasWithOutputs returns (the fields this picker reads). */
interface ListedOutput {
  id: string;
  name: string;
  unit?: string | null;
  storageMode?: string | null;
  active?: boolean | null;
  channels?: unknown;
}

interface ListedSchema {
  id: string;
  name: string;
  modelKind?: string | null;
  organizationId?: string | null;
  owner?: string | null;
  outputs?: { items?: (ListedOutput | null)[] } | null;
}

interface ModelOutputFieldProps {
  fieldKey: string;
  field: Pick<FieldDefinition, "label" | "description" | "validation"> | null | undefined;
  value: ModelOutputFieldValue | null | undefined;
  onChange: (value: ModelOutputFieldValue | null) => void;
  error?: string;
  context?: DatasetFieldContext;
}

/** Column names of a FILES output: its declared channels (the forecast file's columns), when it has any. */
const asStringList = (raw: unknown): string[] => {
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = [];
    }
  }
  return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === "string") : [];
};

/** Every model the viewer can read, with its outputs: fetched on each mount (a cache here would miss a model created
 * a moment ago in the same page session, and would survive a sign-out). */
async function loadModels(): Promise<PickableModel[]> {
  const client = generateClient();
  const models: PickableModel[] = [];
  let nextToken: string | null = null;
  do {
    const { data } = (await client.graphql({ query: listProcessedModelSchemasWithOutputs, variables: { limit: 200, nextToken } })) as {
      data?: { listProcessedModelSchemas?: { items?: (ListedSchema | null)[]; nextToken?: string | null } };
    };
    const page = data?.listProcessedModelSchemas;
    for (const schema of (page?.items ?? []).filter((item): item is ListedSchema => Boolean(item))) {
      models.push({
        id: schema.id,
        name: schema.name,
        modelKind: schema.modelKind === "HISTORICAL" ? "HISTORICAL" : "FORECAST",
        organizationId: schema.organizationId ?? null,
        owner: schema.owner ?? null,
        outputs: (schema.outputs?.items ?? [])
          // A historical model's data lives in the dataset it writes (DATASET): link that dataset with a dataset
          // field instead. Only forecast outputs (FILES) are read through a model_output field.
          .filter((o): o is ListedOutput => Boolean(o) && o!.storageMode !== "DATASET" && o!.storageMode !== "SERIES")
          .map((o) => ({
            id: o.id,
            name: o.name,
            unit: o.unit ?? null,
            storageMode: "FILES" as const,
            active: o.active !== false,
            columns: asStringList(o.channels),
          })),
      });
    }
    nextToken = page?.nextToken ?? null;
  } while (nextToken);
  return models.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The `model_output` field type (docs/architecture/MODEL_OUTPUT_INPUTS.md): another model's forecast output the user
 * can read, and the columns this model processes. A forecast (FILES) output offers its declared channels, or lets
 * the columns be typed when it declares none. A historical model's data is in a dataset and is read with a dataset
 * field, so its outputs are never offered here. The model being edited never offers itself (a model can't read its
 * own output); the run function refuses longer loops.
 */
const ModelOutputField: React.FC<ModelOutputFieldProps> = ({ fieldKey, field, value, onChange, error, context }) => {
  const [models, setModels] = useState<PickableModel[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // The model picked before an output is: kept here, not in the value, so a required field stays empty (and the
  // wizard's required check fails) until an output is chosen
  const [draftModelId, setDraftModelId] = useState<string | null>(value?.modelId ?? null);
  // The typed columns of a forecast output with no declared channels: a draft only while the box is focused; the
  // saved value is what it shows otherwise, so switching outputs never leaves stale text beside an empty value
  const [typedDraft, setTypedDraft] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadModels()
      .then((rows) => !cancelled && setModels(rows))
      .catch((err) => !cancelled && setLoadError(err?.message || "Could not load the models."));
    return () => {
      cancelled = true;
    };
  }, []);

  const config = field?.validation?.modelOutputConfig || {};
  const multi = config.columns !== "single";
  // storageModes on an older template may still say SERIES; every linkable output is FILES now
  const pickable = useMemo(() => {
    return (models ?? [])
      .filter((m) => m.id !== context?.editingModelId)
      .map((m) => ({ ...m, outputs: m.outputs.filter((o) => o.active) }))
      .filter((m) => m.outputs.length > 0);
  }, [models, context?.editingModelId]);

  const pickedModelId = value?.modelId ?? draftModelId;
  const pickedModel = pickable.find((m) => m.id === pickedModelId) ?? null;
  const pickedOutput = pickedModel?.outputs.find((o) => o.id === value?.outputParamId) ?? null;
  const ticked = Array.isArray(value?.columns) ? (value?.columns as string[]) : [];
  const columns = pickedOutput?.columns ?? [];

  // No output picked = no value (the field is empty, as a dataset field without a dataset is)
  const emit = (model: PickableModel | null, output: PickableOutput | null, nextColumns: string[]) => {
    setDraftModelId(model?.id ?? null);
    setTypedDraft(null);
    if (!model || !output) {
      onChange(null);
      return;
    }
    onChange({ type: "model_output", modelId: model.id, outputParamId: output.id, storageMode: output.storageMode, columns: multi ? nextColumns : nextColumns.slice(0, 1) });
  };

  const pickModel = (modelId: string) => {
    const model = pickable.find((m) => m.id === modelId) ?? null;
    setDraftModelId(model?.id ?? null);
    const output = model?.outputs.length === 1 ? model.outputs[0] : null;
    // Keep the ticked columns the new output also has, so swapping between like outputs keeps them
    emit(model, output, output ? ticked.filter((c) => output.columns.includes(c)) : []);
  };

  const pickOutput = (outputId: string) => {
    const output = pickedModel?.outputs.find((o) => o.id === outputId) ?? null;
    emit(pickedModel, output, output ? ticked.filter((c) => output.columns.includes(c)) : []);
  };

  const toggleColumn = (column: string) => {
    if (!pickedOutput) return;
    const next = multi ? (ticked.includes(column) ? ticked.filter((c) => c !== column) : [...ticked, column]) : [column];
    emit(pickedModel, pickedOutput, next);
  };

  // The run function's own rule: an output of a model outside this model's organization (or, for a personal model,
  // another person's) is refused unless the owner is an Admin, who gets the amber note instead
  const verdictFor = (model: { organizationId?: string | null } | null | undefined) =>
    datasetPickVerdict({ ...(context ?? {}), datasetOrganizationId: model?.organizationId });
  const verdict = pickedModel ? verdictFor(pickedModel) : null;

  return (
    <div className="space-y-3" data-testid={`model-output-field-${fieldKey}`}>
      <select value={pickedModelId || ""} onChange={(e) => pickModel(e.target.value)} className={`eai-select w-full ${error ? "border-eai-danger" : ""}`} aria-label={field?.label || "Model"} data-testid="model-output-model-select">
        <option value="">{models === null && !loadError ? "Loading models…" : "Choose a model"}</option>
        {pickable.map((m) => {
          const refused = verdictFor(m).refused;
          return (
            <option key={m.id} value={m.id} disabled={Boolean(refused)}>
              {m.name} · {m.modelKind === "HISTORICAL" ? "historical" : "forecast"}
              {refused ? ` · ${refused}` : ""}
            </option>
          );
        })}
      </select>
      {models !== null && pickable.length === 0 && <p className="text-xs text-gray-500">No model outputs you can read yet.</p>}
      {loadError && <p className="text-xs text-eai-danger-text">{loadError}</p>}

      {pickedModel && (
        <select value={value?.outputParamId || ""} onChange={(e) => pickOutput(e.target.value)} className="eai-select w-full" aria-label="Output" data-testid="model-output-output-select">
          <option value="">Choose an output</option>
          {pickedModel.outputs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.unit ? ` (${o.unit})` : ""} · forecast files
            </option>
          ))}
        </select>
      )}

      {pickedOutput && (
        <div className="space-y-1" data-testid="model-output-columns">
          <span className="block text-xs font-medium text-gray-700">{multi ? "Columns to process" : "Column to process"}</span>
          {columns.length === 0 ? (
            (
              <input
                type="text"
                value={typedDraft ?? ticked.join(", ")}
                onChange={(e) => {
                  setTypedDraft(e.target.value);
                  const next = e.target.value.split(",").map((c) => c.trim()).filter(Boolean);
                  onChange({ type: "model_output", modelId: pickedModel!.id, outputParamId: pickedOutput.id, storageMode: pickedOutput.storageMode, columns: multi ? next : next.slice(0, 1) });
                }}
                onBlur={() => setTypedDraft(null)}
                placeholder={multi ? "Column names, comma separated (as in the forecast file)" : "Column name (as in the forecast file)"}
                aria-label={multi ? "Columns to process" : "Column to process"}
                className="eai-input w-full"
                data-testid="model-output-typed-columns"
              />
            )
          ) : (
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {columns.map((column) => (
                <label key={column} className="flex items-center gap-2 text-sm text-gray-700">
                  <input type={multi ? "checkbox" : "radio"} name={`${fieldKey}-columns`} checked={ticked.includes(column)} onChange={() => toggleColumn(column)} data-testid={`column-${column}`} />
                  {column}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      {verdict?.refused && (
        <p className="flex items-center gap-1 text-xs text-eai-danger-text" data-testid="model-output-refused">
          <FiAlertTriangle size={12} aria-hidden />
          This model can't read that model's outputs: {verdict.refused}. Pick another, or change the owner above.
        </p>
      )}
      {verdict?.crossOrg && !verdict.refused && (
        <p className="flex items-center gap-1 text-xs text-eai-warning-text" data-testid="model-output-cross-org">
          <FiAlertTriangle size={12} aria-hidden />
          This model doesn't own that model. Only an Admin owner can link it, and the model page will say so.
        </p>
      )}
    </div>
  );
};

export default ModelOutputField;
